/**
 * Recoil atoms for menu scope state management
 * Provides persistent state management for menu scope selection and organization access
 *
 * @module hooks/menuScope/atoms
 */
import { atom, AtomEffect } from 'recoil';

import { MENU_SCOPE, LOCAL_STORAGE_MENU_SCOPE_KEY, LOCAL_STORAGE_ORG_ACCESS_KEY } from 'utils/menuMaker/constants';

/**
 * Validates if a value is a valid MENU_SCOPE enum value
 * Used to ensure data integrity when reading from localStorage
 *
 * @param {string | null} value - The value to validate
 * @returns {boolean} True if the value is a valid MENU_SCOPE enum value
 */
const isValidMenuScope = (value: string | null): value is MENU_SCOPE => {
  if (!value) return false;
  return Object.values(MENU_SCOPE).includes(value as MENU_SCOPE);
};

/**
 * Recoil effect for persisting menu scope to localStorage
 * Automatically saves changes to localStorage and handles cleanup
 *
 * @param {Object} params - Recoil effect parameters
 * @param {Function} params.onSet - Callback triggered when atom value changes
 */
const menuScopePersistEffect: AtomEffect<MENU_SCOPE | null> = ({ onSet }) => {
  onSet((newValue) => {
    if (newValue && isValidMenuScope(newValue)) {
      localStorage.setItem(LOCAL_STORAGE_MENU_SCOPE_KEY, newValue);
    } else {
      localStorage.removeItem(LOCAL_STORAGE_MENU_SCOPE_KEY);
    }
  });
};

/**
 * Recoil atom for storing the selected menu scope
 * Manages the current menu scope (ORGANIZATION or STORE) with automatic localStorage persistence
 *
 * Features:
 * - Automatically persists changes to localStorage
 * - Validates stored values on initialization
 * - Defaults to null if no valid scope is stored
 *
 * @type {RecoilState<MENU_SCOPE | null>}
 */
export const menuScopeAtom = atom<MENU_SCOPE | null>({
  key: 'menuMakerScope',
  default: (() => {
    const stored = localStorage.getItem(LOCAL_STORAGE_MENU_SCOPE_KEY);
    return isValidMenuScope(stored) ? stored : null;
  })(),
  effects_UNSTABLE: [menuScopePersistEffect],
});

/**
 * Recoil atom for storing organization access permission
 * Manages whether the current user has access to organization-level features
 *
 * Features:
 * - Automatically persists changes to localStorage
 * - Defaults to null (uninitialized) to allow proper permission-based initialization
 * - Used to control visibility of organization scope option
 * - Will be set to the correct value by useMenuScopeEffects based on user permissions
 *
 * @type {RecoilState<boolean | null>}
 */
export const organizationAccessAtom = atom<boolean | null>({
  key: 'menuMakerOrganizationAccess',
  default: (() => {
    // Don't rely solely on localStorage for initial state
    // Return null to indicate uninitialized state
    // This allows useMenuScopeEffects to set the correct value based on user permissions
    const stored = localStorage.getItem(LOCAL_STORAGE_ORG_ACCESS_KEY);

    // Only use localStorage value if it exists and is explicitly set
    // This prevents false negatives when localStorage is empty or undefined
    if (stored === 'true') {
      return true;
    }
    if (stored === 'false') {
      return false;
    }

    // Return null for uninitialized state - will be set by useMenuScopeEffects
    return null;
  })(),
  effects_UNSTABLE: [
    ({ onSet }) => {
      onSet((hasAccess) => {
        // Only persist non-null values to localStorage
        if (hasAccess !== null) {
          localStorage.setItem(LOCAL_STORAGE_ORG_ACCESS_KEY, String(hasAccess));
        }
      });
    },
  ],
});
